// Native (Capacitor) bootstrap.
//
// IMPORTANT: every behaviour here is gated behind Capacitor.isNativePlatform().
// On the web build initNative() returns immediately and nothing changes, so the
// existing web experience is untouched.
import { Capacitor } from '@capacitor/core';

export function isNative() {
  return Capacitor.isNativePlatform();
}

export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;

  // 1) Status bar: don't draw the WebView under the status bar, give it the
  //    brand background and light icons. With overlay:false the
  //    env(safe-area-inset-top) used by .mobile-header resolves to 0, so the
  //    layout stays consistent across landing / auth / app screens with no
  //    per-page CSS changes.
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Dark }); // dark bg -> light content
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#101820' });
    }
  } catch (e) {
    console.warn('[native] StatusBar init skipped:', e?.message || e);
  }

  // 2) Android hardware back button + deep links (micoach://...).
  try {
    const { App } = await import('@capacitor/app');

    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Deep link: route micoach://<path>?<query> into the SPA via the History
    // API so React Router picks it up client-side (no full reload).
    App.addListener('appUrlOpen', ({ url }) => {
      if (!url) return;
      try {
        const u = new URL(url);
        // For micoach://ajustes?x the URL parser puts "ajustes" in host, so
        // rebuild the SPA path from host + pathname and collapse slashes.
        let path = (u.host ? '/' + u.host : '') + (u.pathname || '');
        if (!path.startsWith('/')) path = '/' + path;
        const target = path.replace(/\/{2,}/g, '/') + (u.search || '') + (u.hash || '');
        window.history.pushState({}, '', target || '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (err) {
        console.warn('[native] appUrlOpen parse failed:', err?.message || err);
      }
    });
  } catch (e) {
    console.warn('[native] App listeners skipped:', e?.message || e);
  }

  // 3) Lock the viewport scale inside the app so the WebView renders 1:1 and
  //    doesn't auto-zoom or allow pinch-zoom. Done here (native only) so the
  //    web's index.html viewport stays untouched.
  try {
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp) {
      vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover');
    }
    document.documentElement.style.webkitTextSizeAdjust = '100%';
  } catch (e) {
    /* noop */
  }

  // 4) Hide the splash once the web app has booted.
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    /* splash plugin optional */
  }
}
